import {
  CarbonAvoidanceProjectType,
  ImpactType,
  pluralise,
  User,
  Wordpress,
} from "@ecologi/shared";
import {
  DarkLinkText,
  DisplayProject,
  HowCalculatedModal,
} from "./HowCalculatedModal";
import { Typography } from "../Typography";
import { DiversifyImpactCard } from "../DiversifyImpactCard";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  projectsIncludingTrees: Wordpress.Project[];
  user: User;
};

export const HowCalculatedModalTrees = ({
  projectsIncludingTrees,
  user,
  ...props
}: Props) => {
  const projectTreesContributionAggregate =
    user.projectTreesContributionAggregate || [];

  const rows: DisplayProject[] = projectTreesContributionAggregate.flatMap(
    ({ projectId, treeNumber }) => {
      const currentProject = projectsIncludingTrees.find(
        (p) => p.id === projectId
      );
      if (!currentProject) return [];

      return [
        {
          type: CarbonAvoidanceProjectType.Forestry, // TODO: [Impact-Types] Should become a tree-based type
          title: currentProject.title,
          quantity: String(treeNumber),
          location: currentProject.location,
          slug: currentProject.slug,
        },
      ];
    }
  );

  const title = (
    <>
      <strong>{user.groupName || user.fullName()}</strong>
      {user.isFamilyPlan ? " have " : " has "}
      funded the planting of{" "}
      <strong>
        {user.totalNumberOfTrees().toLocaleString()}{" "}
        {pluralise(user.totalNumberOfTrees(), "tree", false)}
      </strong>{" "}
      across{" "}
      <strong>
        {rows.length} {pluralise(rows.length, "project", false)}.
      </strong>
    </>
  );

  const explainer = (
    <>
      <Typography use="body1">
        Healthy trees sequester CO2, making them climate champions from the
        moment they’re planted. They also help to restore healthy ecosystems,
        sequestering and storing even more carbon in soils and plant biomass, as
        well as boosting biodiversity.
      </Typography>
      <Typography use="body1">
        Reforestation projects provide co-benefits for local people which can
        promote employment, gender equality, new income streams, more diverse
        food sources to support a healthier diet, and improved community
        facilities.
      </Typography>
      <Typography use="body1">
        Ecologi only supports responsible tree planting. This means supporting
        community-led projects where ecologically appropriate species are
        planted at the right place and time, and where trees are guarded from
        natural and man-made threats.
      </Typography>
      <Typography use="body1">
        Importantly, while trees are a wonderful climate solution, the{" "}
        <DarkLinkText
          as="a"
          use="inlineLink"
          target="_blank"
          href="https://ecologi.com/articles/blog/why-we-dont-use-tree-planting-for-carbon-offsetting"
          rel="noopener noreferrer"
        >
          carbon absorbed by our trees is not taken into account as carbon
          avoidance
        </DarkLinkText>{" "}
        here at Ecologi.
      </Typography>
    </>
  );

  const projectsIntro = (
    <Typography use="body1">
      This breakdown shows how the trees you've funded are distributed across
      our projects. It’s accurate for the UK, US, Australia and Brazil, and
      estimated for all other regions.{" "}
      <DarkLinkText
        as="a"
        use="inlineLink"
        target="_blank"
        href="https://ecologi.com/articles/blog/where-are-my-trees"
        rel="noopener noreferrer"
      >
        Find out more
      </DarkLinkText>{" "}
    </Typography>
  );

  return (
    <HowCalculatedModal
      type={ImpactType.TREES}
      noImpactTitle="You have not yet funded any tree planting projects."
      noImpactSlot={<DiversifyImpactCard impactType={ImpactType.TREES} />}
      title={title}
      explanation={explainer}
      projects={rows}
      projectsIntro={projectsIntro}
      {...props}
    ></HowCalculatedModal>
  );
};
